import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Tooltip, TooltipModel } from '@notino/react-styleguide';

import { TrackingContextProvider, useTrackingContext } from '@context/TrackingContext';
import { trackOnVoucherCopyClick } from '@services/ecommerceService';
import { DEFAULT_CURRENCY_SETUP } from '@sharedComponents/Prices';
import { ReactFCV } from '@types';

import { PRICE_FONT_SIZE_IN_REM, TOOLTIP_SHOW_TIME } from './constants';
import { messages } from './messages';
import { IVoucherUnderProductProps } from './model';
import {
  STooltipMessageWrapper,
  StyledDiscountedPrice,
  StyledIconRegularCopy,
  StyledInteractiveArea,
  StyledMessage,
  StyledVoucherCode,
  StyledVoucherUnderProduct,
} from './styled';

export const VoucherUnderProduct: ReactFCV<IVoucherUnderProductProps> = ({
  discountedPrice,
  voucherCode,
  customerVouchers,
  setProductVoucherCode,
  voucherUnderProductLabel,
  quantity,
}) => {
  const { getTimeFromInit } = useTrackingContext();
  const lastPageViewMs = React.useRef(0);
  TrackingContextProvider.useSubscribeToInitTimeReset(() => {
    lastPageViewMs.current = 0;
  });
  const [codeCopied, setCodeCopied] = useState(false);
  const voucherCodeLowerCase = voucherCode.toLowerCase();
  const finalPrice = quantity ? discountedPrice * quantity : discountedPrice;

  const handleCopy = () => {
    navigator.clipboard
      .writeText(voucherCodeLowerCase)
      .then(() => {
        setProductVoucherCode({ productVoucherCode: voucherCodeLowerCase });
        trackOnVoucherCopyClick(getTimeFromInit() - lastPageViewMs.current, voucherCodeLowerCase);
        setCodeCopied(true);
        setTimeout(() => setCodeCopied(false), TOOLTIP_SHOW_TIME);
      })
      .catch(() => {});
  };

  if (!customerVouchers || !voucherUnderProductLabel) {
    return null;
  }

  const voucherAlreadyApplied = customerVouchers.some((v) => v.code.toLowerCase() === voucherCodeLowerCase);
  if (voucherAlreadyApplied) {
    return null;
  }
  return (
    <StyledVoucherUnderProduct>
      <StyledDiscountedPrice
        price={finalPrice}
        isHighLighted={true}
        secondCurrency={{ ...DEFAULT_CURRENCY_SETUP, bold: true, priceSize: PRICE_FONT_SIZE_IN_REM }}
        dualPriceEnabled={true}
        firstCurrency={{ ...DEFAULT_CURRENCY_SETUP, bold: true, priceSize: PRICE_FONT_SIZE_IN_REM }}
      />
      <StyledMessage>
        <FormattedMessage {...voucherUnderProductLabel} />
      </StyledMessage>
      <StyledVoucherCode>{voucherCodeLowerCase}</StyledVoucherCode>
      <Tooltip
        content={
          <STooltipMessageWrapper>
            <FormattedMessage {...(codeCopied ? messages.voucherCopiedTooltipText : messages.copyVoucherTooltipText)} />
          </STooltipMessageWrapper>
        }
        open={codeCopied ? true : undefined}
        position={TooltipModel.Position.topCenter}>
        <StyledInteractiveArea onClick={handleCopy}>
          <StyledIconRegularCopy />
        </StyledInteractiveArea>
      </Tooltip>
    </StyledVoucherUnderProduct>
  );
};
